import React, { useState, useEffect } from 'react';
import { apiService, Testimonial } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Plus, Edit, Trash2, Star, MessageSquare, Quote } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const Testimonials: React.FC = () => {
  const { showToast } = useToast();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTestimonials();
      setTestimonials(data || []);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Normalize booleans
    ['is_featured', 'is_active'].forEach(field => {
      if (fd.has(field)) {
        const val = fd.get(field);
        fd.set(field, val === 'on' || val === 'true' ? 'true' : 'false');
      } else {
        fd.append(field, 'false');
      }
    });

    try {
      if (editingTestimonial) {
        await apiService.updateTestimonial(editingTestimonial.id, fd);
      } else {
        await apiService.createTestimonial(fd);
      }

      setShowForm(false);
      setEditingTestimonial(null);
      showToast('Testimonial saved successfully!', 'success');
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      showToast('Error saving testimonial.', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await apiService.deleteTestimonial(id);
        showToast('Testimonial deleted successfully!', 'success');
        fetchTestimonials();
      } catch (error) {
        console.error('Failed to delete testimonial:', error);
        showToast('Failed to delete testimonial.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Testimonials</h1>
          <p className="text-gray-600">Manage customer feedback and reviews</p>
        </div>
        <button
          onClick={() => { setEditingTestimonial(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Testimonial
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingTestimonial ? 'Edit Testimonial' : 'New Testimonial'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input name="customer_name" defaultValue={editingTestimonial?.customer_name} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Provided</label>
                  <input name="service_name" defaultValue={editingTestimonial?.service_name} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                  <select name="rating" defaultValue={editingTestimonial?.rating || 5} className="w-full border border-gray-200 rounded-lg px-4 py-2">
                    {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                  <input type="file" name="customer_image" accept="image/*" className="block w-full text-xs text-gray-500 file:border file:bg-gray-50 file:rounded" required={!editingTestimonial} />
                  {editingTestimonial?.customer_image && (
                    <div className="mt-2 h-16 w-16 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                      <img src={getImageUrl(editingTestimonial.customer_image) || undefined} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Message</label>
                  <textarea name="review" defaultValue={editingTestimonial?.review} required rows={4} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" name="is_featured" id="t_featured" defaultChecked={editingTestimonial?.is_featured} className="h-5 w-5 text-orange-600 rounded" />
                    <label htmlFor="t_featured" className="text-sm font-medium text-gray-700">Featured</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" name="is_active" id="t_active" defaultChecked={editingTestimonial?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                    <label htmlFor="t_active" className="text-sm font-medium text-gray-700">Active</label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingTestimonial ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((t) => (
          <div key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col group">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-1 text-yellow-400">
                {[...Array(5)].map((_, i) => <Star key={i} className={`h-3 w-3 ${i < t.rating ? 'fill-current' : 'text-gray-200'}`} />)}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditingTestimonial(t); setShowForm(true); }} className="p-1 text-gray-400 hover:text-orange-600 opacity-0 group-hover:opacity-100 transition"><Edit className="h-4 w-4" /></button>
                <button onClick={() => handleDelete(t.id)} className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="relative mb-6">
              <Quote className="h-6 w-6 text-orange-100 absolute -top-2 -left-2" />
              <p className="text-sm text-gray-600 italic relative line-clamp-4 leading-relaxed">"{t.review}"</p>
            </div>
            <div className="mt-auto flex items-center gap-3 pt-4 border-t border-gray-50">
              <img src={getImageUrl(t.customer_image) || undefined} className="w-10 h-10 rounded-full object-cover border" alt="" />
              <div>
                <h4 className="text-sm font-bold text-gray-900">{t.customer_name}</h4>
                <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{t.service_name}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No testimonials found.</p>
        </div>
      )}
    </div>
  );
};

export default Testimonials;
